import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    listPaymentMethods as fetchListPaymentMethods, // Renamed to avoid conflict
    deletePaymentMethod,
    setDefaultPaymentMethod,
    PaymentMethod,
} from "../../services/subscriptionService";
import { Loader2, CheckCircle } from "lucide-react"; // Added CheckCircle for selection
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface PaymentMethodsListProps {
    mode?: "management" | "selection";
    paymentMethods?: PaymentMethod[]; // External data for selection mode
    isLoading?: boolean; // External loading state for selection mode
    selectedPaymentMethodId?: string | null;
    onSelectPaymentMethod?: (id: string) => void;
    hideTitle?: boolean;
}

const PaymentMethodsList: React.FC<PaymentMethodsListProps> = ({
    mode = "management",
    paymentMethods: externalPaymentMethods,
    isLoading: isLoadingExternal,
    selectedPaymentMethodId,
    onSelectPaymentMethod,
    hideTitle = false,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    // Internal data fetching for management mode
    const {
        data: internalPaymentMethods,
        isLoading: isLoadingInternal,
        error,
    } = useQuery({
        queryKey: ["paymentMethods"], // Use the shared queryKey
        queryFn: fetchListPaymentMethods,
        enabled: mode === "management", // Only fetch if in management mode
    });

    const deleteMutation = useMutation({
        mutationFn: deletePaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });
            toast.success(t('paymentMethodDeletedSuccessfully'));
        },
        onError: (err: Error) => {
            toast.error(t('errorDeletingPaymentMethod', { error: err.message }));
        },
    });

    const setDefaultMutation = useMutation({
        mutationFn: setDefaultPaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });
            toast.success(t('defaultPaymentMethodUpdated'));
        },
        onError: (err: Error) => {
            toast.error(t('errorSettingDefaultPaymentMethod', { error: err.message }));
        },
    });

    // Determine data and loading state based on mode
    const currentPaymentMethods =
        mode === "selection" ? externalPaymentMethods : internalPaymentMethods;
    const isLoading =
        mode === "selection" ? isLoadingExternal : isLoadingInternal;

    // Original handlers from the old component, adapted for mutation.isPending
    const handleDelete = (id: string) => {
        if (
            window.confirm(
                t('confirmDeletePaymentMethod')
            )
        ) {
            deleteMutation.mutate(id);
        }
    };

    const handleSetDefault = (id: string) => {
        setDefaultMutation.mutate(id);
    };

    if (isLoading)
        return (
            <div className="flex items-center justify-center p-4">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('loadingPaymentMethods')}
            </div>
        );

    if (mode === "management" && error) {
        return (
            <p className="text-red-500">
                {t('errorLoadingPaymentMethods', { error: error.message })}
            </p>
        );
    }
    if (mode === "selection" && !isLoading && !currentPaymentMethods) {
        return (
            <p className="text-red-500">
                {t('paymentMethodsDataNotProvided')}
            </p>
        );
    }

    if (!currentPaymentMethods || currentPaymentMethods.length === 0) {
        return (
            <p className="text-gray-600 text-center py-4">
                {t('noPaymentMethodsSaved')}
            </p>
        );
    }

    return (
        <div className="space-y-3">
            {!hideTitle && mode === "management" && (
                <h3 className="text-lg font-semibold text-gray-800">
                    {t('yourPaymentMethods')}
                </h3>
            )}
            <ul className="divide-y divide-gray-200">
                {currentPaymentMethods.map((pm) => (
                    <li
                        key={pm.id}
                        className={`py-3 px-2 flex justify-between items-center
                        ${
                            mode === "selection"
                                ? "cursor-pointer hover:bg-gray-100 rounded-md"
                                : ""
                        }
                        ${
                            mode === "selection" &&
                            pm.id === selectedPaymentMethodId
                                ? "bg-blue-50 border-l-4 border-blue-500"
                                : ""
                        }
                       `}
                        onClick={() =>
                            mode === "selection" &&
                            onSelectPaymentMethod?.(pm.id)
                        }
                    >
                        <div className="flex-grow">
                            <p className="font-medium text-gray-700">
                                {pm.cardBrand.toUpperCase()} {t('endingIn')}{" "}
                                {pm.last4}
                                {pm.isDefault && (
                                    <span
                                        className={`ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full
                                ${
                                    pm.id === selectedPaymentMethodId &&
                                    mode === "selection"
                                        ? "bg-blue-100 text-blue-700"
                                        : "bg-green-100 text-green-700"
                                }`}
                                    >
                                        {t('default')}
                                    </span>
                                )}
                            </p>
                            <p className="text-sm text-gray-500">
                                {t('expires')}: {String(pm.expMonth).padStart(2, "0")}/
                                {pm.expYear}
                            </p>
                        </div>
                        {mode === "selection" &&
                            pm.id === selectedPaymentMethodId && (
                                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 ml-3" />
                            )}
                        {mode === "management" && (
                            <div className="space-x-2 flex-shrink-0 ml-3">
                                {!pm.isDefault && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSetDefault(pm.id);
                                        }}
                                        disabled={setDefaultMutation.isPending} // Corrected from isPending
                                        className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 px-2 py-1 rounded hover:bg-blue-50"
                                    >
                                        {t('setAsDefault')}
                                    </button>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(pm.id);
                                    }}
                                    disabled={deleteMutation.isPending} // Corrected from isPending
                                    className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 px-2 py-1 rounded hover:bg-red-50"
                                >
                                    {t('delete')}
                                </button>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default PaymentMethodsList;
